import { FileType } from "./form-element";
import { PrimitiveDataType, TimeFormat } from "./generic";
import {
  AppAndFormAction,
  AppAndFormStatus,
  CountryAndDialCode,
  CreatedBy,
  CurrencyCode,
  Entity,
  FormResponseMetaData,
} from "./gql";
import {
  AGGREGATION_OPERATOR,
  LOGICAL_OPERATOR,
  RELATIONAL_OPERATOR,
  SECONDARY_RELATIONAL_OPERATOR,
} from "./operator";
import { AppData } from "./app";
import { Organization } from "./organization";
import { PhoneNumberValue } from "@components/phone-number";
import { CurrencyValue } from "@components/currency";
import { User } from "@archetypes/auth";
import { FormResponse, ResponseTemplateData } from "./responses";

export const enum RatingType {
  Star = "Star",
  Smiley = "Smiley",
  Thumb = "Thumb",
  Number = "Number",
}

export const enum DateFormat {
  dayMonthYear = "dd/MM/yyyy",
  monthDayYear = "MM/dd/yyyy",
  yearMonthDay = "yyyy/MM/dd",
}

export const enum ResetCounterFrequency {
  DAY = "Daily",
  MONTH = "Monthly",
  YEAR = "Yearly",
  WEEK = "Weekly",
}

export const ResetCounterFrequencies = [
  ResetCounterFrequency.DAY,
  ResetCounterFrequency.WEEK,
  ResetCounterFrequency.MONTH,
  ResetCounterFrequency.YEAR,
];

export const DateFormats = [
  DateFormat.dayMonthYear,
  DateFormat.monthDayYear,
  DateFormat.yearMonthDay,
];

export const enum ElementType {
  // Elements with User Input
  LocationPicker = "LocationPicker",
  MultiChoice = "MultiChoice",
  FileUpload = "FileUpload",
  Rating = "Rating",
  DatePicker = "DatePicker",
  DateRangePicker = "DateRangePicker",
  Slider = "Slider",
  SingleLineInputBox = "SingleLineInputBox",
  SingleChoice = "SingleChoice",
  MultiLineInputBox = "MultiLineInputBox",
  SingleDropdownSelect = "SingleDropdownSelect",
  MultipleDropdownSelect = "MultipleDropdownSelect",
  RichText = "RichText",
  Signature = "Signature",
  YesNo = "Yes_No",
  Ranking = "Ranking",
  Name = "Name",
  Frequency = "Frequency",
  Email = "Email",
  Link = "Link",
  MobileNumber = "MobileNumber",
  Currency = "Currency",
  Users = "Users",
  Entities = "Entities",
  TermsAndConditons = "TermsAndConditions",
  TimePicker = "TimePicker",
  ImageUpload = "ImageUpload",
  DataModel = "DataModel",
  Formula = "Formula",
  Number = "Number",
  Aggregator = "Aggregator",
  Repeater = "Repeater",
  // Elements without User Input
  UniqueId = "UniqueId",
  PageBreak = "PageBreak",
  Expression = "Expression",
  PreviewImage = "PreviewImage",
  Video = "Video",
}

export const ElementTypes = [
  ElementType.Users,
  ElementType.Repeater,
  ElementType.Frequency,
  ElementType.Aggregator,
  ElementType.Number,
  ElementType.Entities,
  ElementType.LocationPicker,
  ElementType.MultiChoice,
  ElementType.FileUpload,
  ElementType.Rating,
  ElementType.DatePicker,
  ElementType.Slider,
  ElementType.SingleLineInputBox,
  ElementType.YesNo,
  ElementType.SingleChoice,
  ElementType.MultiLineInputBox,
  ElementType.SingleDropdownSelect,
  ElementType.MultipleDropdownSelect,
  ElementType.RichText,
  ElementType.Signature,
  ElementType.Expression,
  ElementType.Video,
  ElementType.YesNo,
  ElementType.Ranking,
  ElementType.Link,
  ElementType.Email,
  ElementType.Name,
  ElementType.MobileNumber,
  ElementType.Currency,
  ElementType.MobileNumber,
  ElementType.TermsAndConditons,
  ElementType.TimePicker,
  ElementType.PreviewImage,
  ElementType.ImageUpload,
  ElementType.PageBreak,
  ElementType.UniqueId,
  ElementType.DataModel,
  ElementType.Repeater,
  ElementType.Formula,
  ElementType.DateRangePicker,
] as const;

type IElementTypeAggregationOperatorMap = {
  [id in ElementType]: AGGREGATION_OPERATOR[];
};

export const ElementTypeAggregationOperatorMap: IElementTypeAggregationOperatorMap = {
  [ElementType.Email]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.Currency]: [
    AGGREGATION_OPERATOR.SUM,
    AGGREGATION_OPERATOR.MIN,
    AGGREGATION_OPERATOR.MAX,
    AGGREGATION_OPERATOR.COUNT,
    AGGREGATION_OPERATOR.MEAN,
    AGGREGATION_OPERATOR.MEDIAN,
  ],
  [ElementType.TermsAndConditons]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.Users]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.Repeater]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.Frequency]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.Aggregator]: [
    AGGREGATION_OPERATOR.SUM,
    AGGREGATION_OPERATOR.MIN,
    AGGREGATION_OPERATOR.MAX,
    AGGREGATION_OPERATOR.COUNT,
    AGGREGATION_OPERATOR.MEAN,
    AGGREGATION_OPERATOR.MEDIAN,
  ],
  [ElementType.Number]: [
    AGGREGATION_OPERATOR.SUM,
    AGGREGATION_OPERATOR.MIN,
    AGGREGATION_OPERATOR.MAX,
    AGGREGATION_OPERATOR.COUNT,
    AGGREGATION_OPERATOR.MEAN,
    AGGREGATION_OPERATOR.MEDIAN,
  ],
  [ElementType.Entities]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.LocationPicker]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.MultiChoice]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.FileUpload]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.Rating]: [
    AGGREGATION_OPERATOR.SUM,
    AGGREGATION_OPERATOR.MIN,
    AGGREGATION_OPERATOR.MAX,
    AGGREGATION_OPERATOR.COUNT,
    AGGREGATION_OPERATOR.MEAN,
    AGGREGATION_OPERATOR.MEDIAN,
  ],
  [ElementType.DatePicker]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.DateRangePicker]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.Slider]: [
    AGGREGATION_OPERATOR.SUM,
    AGGREGATION_OPERATOR.MIN,
    AGGREGATION_OPERATOR.MAX,
    AGGREGATION_OPERATOR.COUNT,
    AGGREGATION_OPERATOR.MEAN,
    AGGREGATION_OPERATOR.MEDIAN,
  ],
  [ElementType.SingleLineInputBox]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.YesNo]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.SingleChoice]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.MultiLineInputBox]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.SingleDropdownSelect]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.MultipleDropdownSelect]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.RichText]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.Signature]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.Expression]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.Video]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.Ranking]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.Link]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.Name]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.MobileNumber]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.TimePicker]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.PreviewImage]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.ImageUpload]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.PageBreak]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.UniqueId]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.DataModel]: [AGGREGATION_OPERATOR.COUNT],
  [ElementType.Formula]: [
    AGGREGATION_OPERATOR.SUM,
    AGGREGATION_OPERATOR.MIN,
    AGGREGATION_OPERATOR.MAX,
    AGGREGATION_OPERATOR.COUNT,
    AGGREGATION_OPERATOR.MEAN,
    AGGREGATION_OPERATOR.MEDIAN,
  ],
};

export const enum LayoutElementType {
  Text = "Text",
  ImageUpload = "ImageUpload",
  ResizableContainer = "ResizableContainer",
  Table = "Table",
  Section = "Section",
  Image = "Image",
  CurrentDate = "currentDate",
  CurrentTime = "CurrentTime",
  CurrentUserName = "CurrentUserName",
  CurrentUserEmail = "CurrentUserEmail",
  CurrentUserRole = "CurrentUserRole",
}

export const enum ActionType {
  OPEN_FORM = "open_form",
  OPEN_TEMPLATE = "open_template",
  EXTERNAL_LINK = "external_link",
}

export const enum WorkerMessageType {
  EVALUATE_DEPENDENTS = "EVALUATE_DEPENDENTS",
  EVALUATE_RESULT = "EVALUATE_RESULT",
}
export const enum UniqueIdLength {
  THREE = 3,
  FOUR,
  FIVE,
  SIX,
  SEVEN,
  EIGHT,
  NINE,
  TEN,
}

export const UniqueIdLengths = [
  UniqueIdLength.THREE,
  UniqueIdLength.FOUR,
  UniqueIdLength.FIVE,
  UniqueIdLength.SIX,
  UniqueIdLength.SEVEN,
  UniqueIdLength.EIGHT,
  UniqueIdLength.NINE,
  UniqueIdLength.TEN,
];

export interface UIElement {
  title: string;
  type: ElementType;
  visible: boolean;
  _id: string;
}

export type Option = {
  id: string;
  value: string;
};

export type OptionWithId = Pick<Option, "id">;

export type PageContentTab = Omit<Page, "questions" | "id">;

export const enum CAMERA_FACING_MODE {
  REAR = "Rear",
  FRONT = "Front",
}

export const enum RESPONSE_VIEW_MODE {
  TABLE = "TABLE",
  CARD = "CARD",
  DROPDOWN = "DROPDOWN",
  CHOICE = "CHOICE",
}

export const enum NUMBER_SYSTEM {
  INDIAN = "Indian",
  INTERNATIONAL = "International",
  GERMAN = "German",
  FRENCH = "French",
  ARABIC = "Arabic",
  SPANISH = "Spanish",
  ITALIAN = "Italian",
}

export const NumberSystems = [
  NUMBER_SYSTEM.INTERNATIONAL,
  NUMBER_SYSTEM.INDIAN,
  NUMBER_SYSTEM.GERMAN,
  NUMBER_SYSTEM.FRENCH,
  NUMBER_SYSTEM.ARABIC,
  NUMBER_SYSTEM.SPANISH,
  NUMBER_SYSTEM.ITALIAN,
];

export const enum UserFilterType {
  ALL_USERS = "All Users",
  ALL_ADMINS = "All Admins",
  SPECIFIC_GROUPS = "Specific Groups",
}

export const UserFilterTypes = [
  UserFilterType.ALL_USERS,
  UserFilterType.ALL_ADMINS,
  UserFilterType.SPECIFIC_GROUPS,
];

export type UserFilter = { type: string; groupId?: string };

export type QuestionContent = {
  label: string; // Label of the question
  required?: boolean; // Is it a mandatory field ?
  requiredErrorMessage?: string; // If the field is mandatory, the required error message
  placeholder?: string; // Placeholder text
  options?: string[]; // options in case of multiple/single choice
  randomiseOptions?: boolean; // boolean value whether to randomise or not
  readOnly?: boolean;
  defaultValue?: FormValue;
  hintEnabled?: boolean; // Hint enabled for a question
  hintMessage?: string; // Hint message for the question
  verifyForOTP?: boolean;
  visible: boolean;
  frequency?: {
    allowIntervalType?: boolean;
    allowAnytime?: boolean;
    allowOnCompletionOf?: boolean;
  };
  aggregator?: {
    offlineAggregation?: boolean;
    operator: AGGREGATION_OPERATOR;
    questionId?: string;
    fieldId?: string;
    questionValue?: DataModelValue[]; // Value for questionId locally
    appId: string;
    formId: string;
    filter?: ResponseFilter;
    updatedFilter?: ResponseFilter;
    updatePrevResponse?: boolean;
  };
  number?: {
    numberSystem?: NUMBER_SYSTEM;
  };
  user?: {
    filterType: UserFilterType;
    groupIds?: string[];
    isMultiChoice?: boolean;
  };
  entity?: {
    isMultiChoice?: boolean;
  };
  boolean?: {
    yesLabel?: string;
    noLabel?: string;
  };
  date?: {
    format?: DateFormat;
    allowTimeSelection?: boolean;
    allowRange?: boolean;
  };
  dateRange?: {
    format?: DateFormat;
    allowRange?: boolean;
  };
  range?: {
    isDualSlider: boolean;
    startValue: number;
    endValue: number;
    stepSize: number;
    leftLabel?: string;
    middleLabel?: string;
    rightLabel?: string;
  };
  video?: {
    videoUrl?: string;
    videoHeight?: number;
  };
  rating?: {
    quantity: number;
    type: RatingType;
    goodRatingLabel?: string;
    badRatingLabel?: string;
  };
  fileUpload?: {
    fileTypes: FileType[];
    maxFiles: number;
  };
  imageUpload?: {
    maxFiles: number;
    allowUploadFromGallery: boolean;
    cameraFacingMode: CAMERA_FACING_MODE;
    allowSwappingCameraFacingMode: boolean;
    aspectRatio: AspectRatio;
  };
  matrix?: {
    multiple?: boolean;
    cols?: string[];
    rows?: string[];
  };
  name?: {
    showMiddleName: boolean;
    firstNamePlaceholder?: string;
    middleNamePlaceholder?: string;
    lastNamePlaceholder?: string;
    firstNameRequired: boolean;
    lastNameRequired: boolean;
    middleNameRequired: boolean;
    firstNameRequiredErrorMessage?: string;
    middleNameRequiredErrorMessage?: string;
    lastNameRequiredErrorMessage?: string;
  };
  phoneNumber?: {
    allowChangingDialCode: boolean;
    defaultValue?: CountryAndDialCode;
  };
  currency?: {
    allowChangingCurrencyCode: boolean;
    defaultValue?: CurrencyCode;
  };
  termsAndConditions?: {
    content: string;
    declaration: string;
  };
  timePicker?: {
    timeFormat: TimeFormat;
  };
  previewImageUrl?: string;
  link?: string;
  uniqueId?: {
    prefix?: string;
    postfix?: string;
    maxLength?: UniqueIdLength;
    counterStartsFrom?: number;
    allowReset?: boolean;
    resetFrequency?: ResetCounterFrequency;
  };
  locationPicker?: {
    geofenceConfig: {
      enabled: boolean;
      address?: string;
      longitude?: number;
      latitude?: number;
      radius?: number;
    };
  };
  dataModel?: {
    appId: string;
    formId?: string;
    responseTemplateId?: string;
    allowMultiSelect?: boolean;
    filter?: ResponseFilter; // raw filter
    updatedFilter?: ResponseFilter; // updated filter with form values
    viewMode: RESPONSE_VIEW_MODE;
    displayField?: string;
    displayFieldType?: ElementType;
    createNewRecordForEachData?: boolean;
    formIdReset?: boolean;
    responseTemplateIdReset?: boolean;
  };
  repeater?: {
    appId: string;
    formId?: string;
    responseTemplateId?: string;
    allowSelectingOldRecord?: boolean;
    allowCreatingNewRecord?: boolean;
    relationshipFieldId?: string;
    immutable?: boolean;
    maxRecords?: number;
    minRecords: number;
    formIdReset?: boolean;
    responseTemplateIdReset?: boolean;
    allowCascadeDelete?: boolean;
  };
  formula?: {
    expression: string;
    precision: PRECISION;
    unit?: string;
    updatePrevResponse?: boolean;
  };
};

export type Question = {
  id: string;
  type: ElementType;
  title: string;
  content: QuestionContent;
  formId?: string;
};

export const enum FieldActionType {
  "DISPLAY_FIELD" = "DISPLAY_FIELD",
  "HIDE_FIELD" = "HIDE_FIELD",
  "SET_VALUE" = "SET_VALUE",
  "UNSET_VALUE" = "UNSET_VALUE",
  "UPDATE_PROPERTY" = "UPDATE_PROPERTY",
  "EVALUATE_FORMULA_EXPRESSION" = "EVALUATE_FORMULA_EXPRESSION",
  "EVALUATE_DATA_VIEW_FILTER" = "EVALUATE_DATA_VIEW_FILTER",
  "EVALUATE_AGGREGATOR" = "EVALUATE_AGGREGATOR",
  "VALIDATION" = "VALIDATION",
}

export const FieldActionTypes = [
  FieldActionType.DISPLAY_FIELD,
  FieldActionType.HIDE_FIELD,
  FieldActionType.EVALUATE_FORMULA_EXPRESSION,
  FieldActionType.SET_VALUE,
  FieldActionType.UNSET_VALUE,
  FieldActionType.EVALUATE_DATA_VIEW_FILTER,
  FieldActionType.EVALUATE_AGGREGATOR,
  FieldActionType.VALIDATION,
  FieldActionType.UPDATE_PROPERTY,
] as const;

export const enum FieldValidationType {
  "MIN" = "MIN",
  "MAX" = "MAX",
  "MAX_LENGTH" = "MAX_LENGTH",
  "MIN_LENGTH" = "MIN_LENGTH",
  "MUST_EQUAL" = "MUST_EQUAL",
  "MUST_NOT_EQUAL" = "MUST_NOT_EQUAL",
  "MAX_RECORDS" = "MAX_RECORDS",
  "MIN_RECORDS" = "MIN_RECORDS",
}

export const FieldValidationTypes = [
  FieldValidationType.MIN,
  FieldValidationType.MAX,
  FieldValidationType.MAX_LENGTH,
  FieldValidationType.MIN_LENGTH,
  FieldValidationType.MUST_EQUAL,
  FieldValidationType.MUST_NOT_EQUAL,
  FieldValidationType.MAX_RECORDS,
  FieldValidationType.MIN_RECORDS,
] as const;

export type FieldAction = {
  type: FieldActionType;
  validationType?: FieldValidationType;
  validationErrorMessage?: string;
  value?: FormValue;
  valueType?: AnswerType;
  property?: string;
};

export type FieldActionToBeProcessed = FieldAction & { question: Question };

export type RuleNode = {
  questionId: string;
  questionType: ElementType;
  operator: RELATIONAL_OPERATOR;
  value?: string | OptionWithId | OptionWithId[];
};

type LeafNode = LOGICAL_OPERATOR | boolean | RuleNode;
export type RuleFilter = Array<LeafNode | Array<LeafNode>>;

export type FieldRuleFieldSets = {
  questionId: string;
  questionType: ElementType;
  relationalOperator: RELATIONAL_OPERATOR;
  secondaryRelationalOperator?: SECONDARY_RELATIONAL_OPERATOR;
  answer?: FormValue;
  logicalOperator?: LOGICAL_OPERATOR;
};

export type FieldRuleFilter = {
  logicalOperator: LOGICAL_OPERATOR;
  fieldsets: FieldRuleFieldSets[];
};

export type FieldRule = {
  filter?: FieldRuleFilter;
  enabled: boolean;
  name: string;
  action: FieldAction;
  immutable: boolean;
  questionId: string;
  pageId: string;
  id: string;
};

export const enum FieldRuleAction {
  ADD = "ADD",
  UPDATE = "UPDATE",
  DELETE = "DELETE",
}

export const enum FormFieldEvent {
  ONLOAD = "onFormLoad",
  ON_VALUE_CHANGE = "onChange",
  ONSUBMIT = "onSubmit",
}

export type FormEventPayload = {
  type: FormFieldEvent;
  questionId?: string;
  value?: FormValue;
};

export type Page = {
  questions: Question[];
  id: string;
  title: string;
  description?: string;
  allowSubmit: boolean;
  nextButtonText: string;
};

export type FormMetaData = {
  pages: Page[];
  fieldRules: FieldRule[];
  fieldRulesMap: { [ruleId: string]: FieldRule }; // Map of all the field rules
  ruleTriggers: { [trigger: string]: string[] }; // Rules are triggered via certain events like when a form is loaded or a question values changes
  actionsToBeProcessed: FieldActionToBeProcessed[];
  questionsMap: Record<string, Question>;
  childFormRecordsMap: Record<string, DataModelValue[]>;
};

export const enum FormType {
  NORMAL = "NORMAL",
  CHILD = "CHILD",
  LINKED = "LINKED",
  INLINE = "INLINE",
}

export const FormTypeOptions = [
  {
    label: "Normal",
    value: FormType.NORMAL,
  },
  {
    label: "Linked",
    value: FormType.LINKED,
  },
  {
    label: "Child",
    value: FormType.CHILD,
  },
  {
    label: "Inline",
    value: FormType.INLINE,
  },
];

export type UFormData = {
  currentStatus: AppAndFormStatus;
  _id: string;
  formName: string;
  formDescription?: string;
  type: FormType;
  responseTemplates: ResponseTemplateData[];
  formData: FormMetaData;
  elements: UIElement[];
  createdAt: string;
  createdBy: User;
  currentAction: {
    action: AppAndFormAction[];
  };
  company?: Organization;
  app: AppData;
  disableFormAfterMaxSubmissions: number;
  enableCaptcha: boolean;
  enableScoring: boolean;
  publishOn: Date;
  coverImage: string;
  resetFormAfterSubmit: boolean;
  compositeFields?: string[];
  showQuestionNo: boolean;
  unpublishOn: Date;
  submitButtonText: string;
  sendCopyOfResponse: boolean;
  removeBranding: boolean;
  countriesAndDialCodes: CountryAndDialCode[];
  countriesCurrencyCodes: CurrencyCode[];
  users: User[];
  entities: Entity[];
};

export const enum RuleActionType {
  SATISFIED_ACTION,
  UNSATISFIED_ACTION,
}

export const enum RuleCustomActionType {
  OPEN_FORM = "OPEN_FORM",
  OPEN_TEMPLATE = "OPEN_TEMPLATE",
  OPEN_PAGE = "OPEN_PAGE",
  OPEN_LAYOUT = "OPEN_LAYOUT",
  UPDATE_FIELDS = "UPDATE_FIELDS",
  COPY_FIELDS = "COPY_FIELDS",
  EMAIL = "EMAIL",
  SMS = "SMS",
  CALL_AN_API = "CALL_AN_API",
  DELETE_FIELDS = "DELETE_FIELDS",
}

export const enum RULE_TRIGGER {
  RESPONSE_CREATE = "RESPONSE_CREATE",
  RESPONSE_UPDATE = "RESPONSE_UPDATE",
  RESPONSE_DELETE = "RESPONSE_DELETE",
  PAGE_CHANGE = "PAGE_CHANGE",
  INTERVAL = "INTERVAL",
  RESPONSE_DUPLICATE = "RESPONSE_DUPLICATE",
  RECORD_ACTION = "RECORD_ACTION",
  BEFORE_RESPONSE_DELETE = "BEFORE_RESPONSE_DELETE",
  BEFORE_RESPONSE_UPDATE = "BEFORE_RESPONSE_UPDATE",
  COLUMN_ACTION = "COLUMN_ACTION",
}

export const RuleTriggers = [
  RULE_TRIGGER.RESPONSE_CREATE,
  RULE_TRIGGER.RESPONSE_UPDATE,
  RULE_TRIGGER.RESPONSE_DELETE,
  RULE_TRIGGER.PAGE_CHANGE,
  RULE_TRIGGER.INTERVAL,
  RULE_TRIGGER.RESPONSE_DUPLICATE,
  RULE_TRIGGER.RECORD_ACTION,
  RULE_TRIGGER.BEFORE_RESPONSE_DELETE,
  RULE_TRIGGER.BEFORE_RESPONSE_UPDATE,
  RULE_TRIGGER.COLUMN_ACTION,
] as const;
export interface DataModelValue {
  responseId: string;
  responseData?: FormResponse;
  appId?: string;
  formId?: string;
}

export type FormsList = Pick<UFormData, "_id" | "formName" | "responseTemplates" | "type">;

export type FormAddEditData = Pick<UFormData, "formName" | "formDescription" | "_id" | "type">;

export const enum FORMVIEW {
  DESKTOP = "desktop",
  TABLET_POTRAIT = "tablet",
  TABLET_LANDSCAPE = "tablet-landscape",
  MOBILE_POTRAIT = "mobile",
  MOBILE_LANDSCAPE = "mobile-landscape",
}

export const enum Position {
  BEFORE = "Before",
  AFTER = "After",
  BEGINNING = "Beginning",
  END = "End",
  WITHIN = "Within",
}

export interface CloneOrMoveQuestionFormData {
  relativeQuestion: Question;
  position: Position;
}

export const MenuPositionArray = [Position.BEFORE, Position.AFTER, Position.WITHIN];

export interface DateRange {
  start?: string;
  end?: string;
}

export const enum FrequencyType {
  ANYTIME = "ANYTIME",
  INTERVAL = "INTERVAL",
  ON_COMPLETION_OF = "ON_COMPLETION_OF",
}

export const FrequencyTypeArray = [
  FrequencyType.ANYTIME,
  FrequencyType.INTERVAL,
  FrequencyType.ON_COMPLETION_OF,
] as const;

export interface IFrequency {
  type: FrequencyType;
  data: {
    interval?: number;
    intervalType?: IntervalType;
    startDate?: string;
    repeatOnDays?: WeekDay[]; // IntervalType = WEEK, Signifies on which days of the week the frequency should trigger
    repeatOnDay?: number; // IntervalType = MONTH, Signifies which day of the month the frequency should trigger
    repeatOnMonths?: Month[]; // IntervalType = MONTH, Signifies which months the frequency should trigger
    endType?: IntervalEndType; // NEVER / ON / AFTER
    endDate?: string; // IntervalEndType = ON
    occurrences?: number; // IntervalEndType = AFTER
    completionWindowDaysBefore?: number; // Days before due date when user can fill the form
    completionWindowDaysAfter?: number; // Days after due date when user can fill the form
  };
}

export interface UserLocation {
  latitude: number;
  longitude: number;
  address?: string;
}

export interface NameValue {
  firstName: string;
  lastName: string;
}

export type FormValue =
  | PrimitiveDataType
  | DateRange
  | UserLocation
  | number[]
  | string[]
  | NameValue
  | CurrencyValue
  | PhoneNumberValue
  | User[]
  | Entity[]
  | DataModelValue[]
  | IFrequency
  | null;

export enum FormMode {
  PREVIEW = "preview",
  LIVE = "live",
  DESIGN = "desgin",
}
export enum FormViewType {
  PREVIEW = "preview",
  LIVE = "live",
}

export const enum FORM_OPERATION {
  ADD_QUESTION = "ADD_QUESTION",
  MOVE_QUESTION = "MOVE_QUESTION",
  UPDATE_QUESTION = "UPDATE_QUESTION",
  CLONE_QUESTION = "CLONE_QUESTION",
  REMOVE_QUESTION = "REMOVE_QUESTION",
  REMOVE_PAGE = "REMOVE_PAGE",
  UPDATE_PAGE = "UPDATE_PAGE",
  CLONE_PAGE = "CLONE_PAGE",
  MOVE_PAGE = "MOVE_PAGE",
  PAGE_BREAK = "PAGE_BREAK",
  ADD_QUESTION_CONDITION = "ADD_QUESTION_CONDITION",
  UPDATE_QUESTION_CONDITION = "UPDATE_QUESTION_CONDITION",
  DELETE_QUESTION_CONDITION = "DELETE_QUESTION_CONDITION",
  BULK_QUESTION_CONDITION = "BULK_QUESTION_CONDITION",
}

export const enum AspectRatio {
  OneToOne = "1:1",
  FourToThree = "4:3",
  SixteenToNine = "16:9",
}

export const AspectRatioArray = [
  AspectRatio.OneToOne,
  AspectRatio.FourToThree,
  AspectRatio.SixteenToNine,
] as const;

export const enum FormShareMode {
  PUBLIC = "public",
  LIMITED = "limited",
}

export interface ShareFormData {
  mode: FormShareMode;
}

export const ElementTypeCompatibilityMap: Record<ElementType, Set<ElementType>> = {
  [ElementType.SingleLineInputBox]: new Set([
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
    ElementType.RichText,
    ElementType.UniqueId,
  ]),
  [ElementType.Repeater]: new Set([ElementType.Repeater, ElementType.DataModel]),
  [ElementType.Aggregator]: new Set([
    ElementType.Number,
    ElementType.Formula,
    ElementType.Currency,
    ElementType.Aggregator,
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
    ElementType.RichText,
  ]),
  [ElementType.Number]: new Set([
    ElementType.Number,
    ElementType.Formula,
    ElementType.Currency,
    ElementType.Aggregator,
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
    ElementType.RichText,
  ]),
  [ElementType.MultiLineInputBox]: new Set([
    ElementType.MultiLineInputBox,
    ElementType.SingleLineInputBox,
    ElementType.RichText,
  ]),
  [ElementType.RichText]: new Set([ElementType.RichText]),
  [ElementType.SingleChoice]: new Set([
    ElementType.SingleChoice,
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
    ElementType.MultiChoice,
    ElementType.SingleDropdownSelect,
    ElementType.MultipleDropdownSelect,
    ElementType.RichText,
  ]),
  [ElementType.MultiChoice]: new Set([
    ElementType.MultiChoice,
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
    ElementType.MultipleDropdownSelect,
    ElementType.RichText,
  ]),
  [ElementType.SingleDropdownSelect]: new Set([
    ElementType.SingleDropdownSelect,
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
    ElementType.MultipleDropdownSelect,
    ElementType.SingleChoice,
    ElementType.MultiChoice,
    ElementType.RichText,
  ]),
  [ElementType.MultipleDropdownSelect]: new Set([
    ElementType.MultipleDropdownSelect,
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
    ElementType.MultiChoice,
    ElementType.RichText,
  ]),
  [ElementType.Name]: new Set([
    ElementType.Name,
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
    ElementType.RichText,
  ]),
  [ElementType.Email]: new Set([
    ElementType.Email,
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
    ElementType.RichText,
  ]),
  [ElementType.MobileNumber]: new Set([
    ElementType.MobileNumber,
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
    ElementType.RichText,
  ]),
  [ElementType.DatePicker]: new Set([
    ElementType.DatePicker,
    ElementType.MultiLineInputBox,
    ElementType.SingleLineInputBox,
    ElementType.RichText,
  ]),
  [ElementType.DateRangePicker]: new Set([ElementType.DateRangePicker]),
  [ElementType.TimePicker]: new Set([
    ElementType.TimePicker,
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
    ElementType.RichText,
  ]),
  [ElementType.LocationPicker]: new Set([
    ElementType.LocationPicker,
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
    ElementType.RichText,
  ]),
  [ElementType.UniqueId]: new Set([
    ElementType.UniqueId,
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
    ElementType.RichText,
  ]),
  [ElementType.Formula]: new Set([
    ElementType.Formula,
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
    ElementType.Number,
    ElementType.RichText,
    ElementType.Aggregator,
  ]),
  [ElementType.YesNo]: new Set([ElementType.YesNo]),
  [ElementType.Link]: new Set([
    ElementType.Link,
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
    ElementType.RichText,
  ]),
  [ElementType.Rating]: new Set([
    ElementType.Rating,
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
    ElementType.Number,
  ]),
  [ElementType.Ranking]: new Set([
    ElementType.Ranking,
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
  ]),
  [ElementType.Slider]: new Set([ElementType.Slider]),
  [ElementType.Currency]: new Set([
    ElementType.Currency,
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
    ElementType.Number,
    ElementType.RichText,
  ]),
  [ElementType.Users]: new Set([
    ElementType.Users,
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
    ElementType.RichText,
  ]),

  [ElementType.Entities]: new Set([
    ElementType.Entities,
    ElementType.SingleLineInputBox,
    ElementType.MultiLineInputBox,
    ElementType.RichText,
  ]),
  [ElementType.DataModel]: new Set([ElementType.DataModel, ElementType.Repeater]),
  [ElementType.FileUpload]: new Set([ElementType.FileUpload]),
  [ElementType.ImageUpload]: new Set([ElementType.ImageUpload, ElementType.FileUpload]),
  [ElementType.PageBreak]: new Set([]),
  [ElementType.PreviewImage]: new Set([]),
  [ElementType.Expression]: new Set([]),
  [ElementType.TermsAndConditons]: new Set([ElementType.TermsAndConditons, ElementType.YesNo]),
  [ElementType.Signature]: new Set([ElementType.Signature]),
  [ElementType.Frequency]: new Set([ElementType.Frequency]),
  [ElementType.Video]: new Set([]),
};

export const ElementTypeRelationalOperatorMap: Record<ElementType, Array<RELATIONAL_OPERATOR>> = {
  [ElementType.Frequency]: [RELATIONAL_OPERATOR.IS_ANSWERED, RELATIONAL_OPERATOR.IS_NOT_ANSWERED],
  [ElementType.DatePicker]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.LIES_BETWEEN,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
    RELATIONAL_OPERATOR.GREATER_THAN,
    RELATIONAL_OPERATOR.LESS_THAN,
    RELATIONAL_OPERATOR.GREATER_THAN_OR_EQUAL,
    RELATIONAL_OPERATOR.LESS_THAN_OR_EQUAL,
  ],
  [ElementType.DateRangePicker]: [
    RELATIONAL_OPERATOR.LIES_BETWEEN,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
  ],
  [ElementType.TimePicker]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
  ],
  [ElementType.Expression]: [],
  [ElementType.FileUpload]: [
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.FILE_NAME_CONTAINS,
    RELATIONAL_OPERATOR.HAS_FILE_TYPE,
  ],
  [ElementType.LocationPicker]: [
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
  ],
  [ElementType.MultiChoice]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.INCLUDES,
    RELATIONAL_OPERATOR.NOT_INCLUDES,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
  ],
  [ElementType.Users]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.INCLUDES,
    RELATIONAL_OPERATOR.NOT_INCLUDES,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
  ],
  [ElementType.Entities]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.INCLUDES,
    RELATIONAL_OPERATOR.NOT_INCLUDES,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
  ],
  [ElementType.MultipleDropdownSelect]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.INCLUDES,
    RELATIONAL_OPERATOR.NOT_INCLUDES,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
  ],
  [ElementType.MultiLineInputBox]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.INCLUDES,
    RELATIONAL_OPERATOR.NOT_INCLUDES,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
  ],
  [ElementType.Ranking]: [RELATIONAL_OPERATOR.IS_ANSWERED, RELATIONAL_OPERATOR.IS_NOT_ANSWERED],
  [ElementType.Rating]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.GREATER_THAN,
    RELATIONAL_OPERATOR.GREATER_THAN_OR_EQUAL,
    RELATIONAL_OPERATOR.LESS_THAN,
    RELATIONAL_OPERATOR.LESS_THAN_OR_EQUAL,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
  ],
  [ElementType.RichText]: [RELATIONAL_OPERATOR.IS_ANSWERED, RELATIONAL_OPERATOR.IS_NOT_ANSWERED],
  [ElementType.Signature]: [RELATIONAL_OPERATOR.IS_ANSWERED, RELATIONAL_OPERATOR.IS_NOT_ANSWERED],
  [ElementType.SingleChoice]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
  ],
  [ElementType.SingleDropdownSelect]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
  ],
  [ElementType.SingleLineInputBox]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.INCLUDES,
    RELATIONAL_OPERATOR.NOT_INCLUDES,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
  ],
  [ElementType.Number]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.INCLUDES,
    RELATIONAL_OPERATOR.NOT_INCLUDES,
    RELATIONAL_OPERATOR.GREATER_THAN,
    RELATIONAL_OPERATOR.GREATER_THAN_OR_EQUAL,
    RELATIONAL_OPERATOR.LESS_THAN,
    RELATIONAL_OPERATOR.LESS_THAN_OR_EQUAL,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
  ],
  [ElementType.Aggregator]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.INCLUDES,
    RELATIONAL_OPERATOR.NOT_INCLUDES,
    RELATIONAL_OPERATOR.GREATER_THAN,
    RELATIONAL_OPERATOR.GREATER_THAN_OR_EQUAL,
    RELATIONAL_OPERATOR.LESS_THAN,
    RELATIONAL_OPERATOR.LESS_THAN_OR_EQUAL,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
  ],
  [ElementType.Slider]: [RELATIONAL_OPERATOR.IS_ANSWERED, RELATIONAL_OPERATOR.IS_NOT_ANSWERED],
  [ElementType.Video]: [],
  [ElementType.YesNo]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
  ],
  [ElementType.Name]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.INCLUDES,
    RELATIONAL_OPERATOR.NOT_INCLUDES,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
  ],
  [ElementType.Email]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.INCLUDES,
    RELATIONAL_OPERATOR.NOT_INCLUDES,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
  ],
  [ElementType.Link]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.INCLUDES,
    RELATIONAL_OPERATOR.NOT_INCLUDES,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
  ],
  [ElementType.MobileNumber]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.INCLUDES,
    RELATIONAL_OPERATOR.NOT_INCLUDES,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
  ],
  [ElementType.Currency]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
    RELATIONAL_OPERATOR.GREATER_THAN,
    RELATIONAL_OPERATOR.GREATER_THAN_OR_EQUAL,
    RELATIONAL_OPERATOR.LESS_THAN,
    RELATIONAL_OPERATOR.LESS_THAN_OR_EQUAL,
  ],
  [ElementType.TermsAndConditons]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.EXIST,
    RELATIONAL_OPERATOR.NOT_EXIST,
  ],
  [ElementType.PreviewImage]: [],
  [ElementType.ImageUpload]: [
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.FILE_NAME_CONTAINS,
    RELATIONAL_OPERATOR.HAS_FILE_TYPE,
  ],
  [ElementType.PageBreak]: [],
  [ElementType.UniqueId]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
  ],
  [ElementType.DataModel]: [RELATIONAL_OPERATOR.IS_NOT_ANSWERED, RELATIONAL_OPERATOR.IS_ANSWERED],
  [ElementType.Repeater]: [RELATIONAL_OPERATOR.IS_NOT_ANSWERED, RELATIONAL_OPERATOR.IS_ANSWERED],
  [ElementType.Formula]: [
    RELATIONAL_OPERATOR.EQUAL,
    RELATIONAL_OPERATOR.NOT_EQUAL,
    RELATIONAL_OPERATOR.IS_NOT_ANSWERED,
    RELATIONAL_OPERATOR.IS_ANSWERED,
    RELATIONAL_OPERATOR.GREATER_THAN,
    RELATIONAL_OPERATOR.GREATER_THAN_OR_EQUAL,
    RELATIONAL_OPERATOR.LESS_THAN,
    RELATIONAL_OPERATOR.LESS_THAN_OR_EQUAL,
  ],
};

export const enum AnswerType {
  STATIC = "STATIC",
  DYNAMIC = "DYNAMIC",
}

export interface ResponseFilterField {
  questionId: string;
  questionType: ElementType;
  relationalOperator: RELATIONAL_OPERATOR;
  answer?: FormValue;
  answerType?: AnswerType;
  secondaryRelationalOperator?: SECONDARY_RELATIONAL_OPERATOR;
}
export interface ResponseFilter {
  logicalOperator: LOGICAL_OPERATOR;
  fieldsets: Array<ResponseFilterField>;
}

export const enum SortOrder {
  ASCENDING = "ASC",
  DESCENDING = "DESC",
}

export const SortOrderTypes = [SortOrder.ASCENDING, SortOrder.DESCENDING] as const;

export function getSortOrderLabel(order: SortOrder) {
  switch (order) {
    case SortOrder.ASCENDING:
      return "Ascending";
    case SortOrder.DESCENDING:
      return "Descending";
  }
}

export const enum IntervalEndType {
  NEVER = "NEVER",
  ON = "ON",
  AFTER = "AFTER",
}

export const FrequencyEndTypeArray = [
  IntervalEndType.NEVER,
  IntervalEndType.ON,
  IntervalEndType.AFTER,
];

export const enum WeekDay {
  SUNDAY = "SUN",
  MONDAY = "MON",
  TUESDAY = "TUE",
  WEDNESDAY = "WED",
  THURSDAY = "THU",
  FRIDAY = "FRI",
  SATURDAY = "SAT",
}
export const WeekDayArray = [
  WeekDay.SUNDAY,
  WeekDay.MONDAY,
  WeekDay.TUESDAY,
  WeekDay.WEDNESDAY,
  WeekDay.THURSDAY,
  WeekDay.FRIDAY,
  WeekDay.SATURDAY,
];

export const enum IntervalType {
  DAY = "DAY",
  WEEK = "WEEK",
  MONTH = "MONTH",
  YEAR = "YEAR",
}

export const IntervalTypeArray = [
  IntervalType.DAY,
  IntervalType.MONTH,
  IntervalType.WEEK,
  IntervalType.YEAR,
];

export const enum Month {
  JAN = "JAN",
  FEB = "FEB",
  MAR = "MAR",
  APR = "APR",
  MAY = "MAY",
  JUN = "JUN",
  JUL = "JUL",
  AUG = "AUG",
  SEP = "SEP",
  OCT = "OCT",
  NOV = "NOV",
  DEC = "DEC",
}

export const MonthsArray = [
  Month.JAN,
  Month.FEB,
  Month.MAR,
  Month.APR,
  Month.MAY,
  Month.JUN,
  Month.JUL,
  Month.AUG,
  Month.SEP,
  Month.OCT,
  Month.NOV,
  Month.DEC,
] as const;

export enum Quarter {
  Q1 = "Q1",
  Q2 = "Q2",
  Q3 = "Q3",
  Q4 = "Q4",
}

export const QuarterArray = [Quarter.Q1, Quarter.Q2, Quarter.Q3, Quarter.Q4] as const;

export const enum PRECISION {
  TWO = 2,
  THREE = 3,
  FOUR = 4,
  FIVE = 5,
}

export const PrecisionArray = [
  PRECISION.TWO,
  PRECISION.THREE,
  PRECISION.FOUR,
  PRECISION.FIVE,
] as const;

export const YesNo = {
  YES: true,
  NO: false,
};

export const YesNoArray = [YesNo.YES, YesNo.NO] as const;

export const enum FieldOrPageOperationType {
  EDIT,
  DELETE,
  CLONE,
  COPY,
  MOVE,
}
export enum ArrangementType {
  CALENDAR = "CALENDAR",
  TABLE = "TABLE",
  KANBAN = "KANBAN",
}
export const ArrangementTypeArray = [
  ArrangementType.CALENDAR,
  ArrangementType.TABLE,
  ArrangementType.KANBAN,
];
export interface GroupByResponse {
  response: {
    responseData: FormResponseMetaData;
    _id: string;
    createdBy: CreatedBy;
    createdAt: string;
    updatedAt: string;
  };
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ListResponseExtraParamsType = Record<string, any>;
export interface previewResponseType {
  title: string;
  listResponseExtraParams?: ListResponseExtraParamsType;
  response?: FormResponseMetaData;
}

export enum TimeUnit {
  Hours = "hrs",
  Minutes = "min",
  Days = "days",
  Seconds = "sec",
  Week = "week",
  Months = "months",
  Year = "year",
}

export interface ChildFormRecordConfig {
  formId: string; // formId of the form to be opened
  questionId: string; // questionId of the parent form
  relationshipFieldId: string; // question id of relationship DM
  allowMultiSelect: boolean; // whether to allow multi select
  responseId?: string; // In case of editing a child record we need the responseId
  addMode?: boolean; // In case of adding a new child record we need to add it to the end of the array
}

export const getYearRange = (range: number): string[] => {
  const currentYear = new Date().getFullYear();
  const start = currentYear - range;
  const end = currentYear + range;
  return Array.from({ length: end - start + 1 }, (_, i) => (start + i).toString());
};

export const enum DefaultDateViewType {
  CURRENT_DAY = "CURRENT_DAY",
  CURRENT_WEEK = "CURRENT_WEEK",
  CURRENT_MONTH = "CURRENT_MONTH",
  CURRENT_YEAR = "CURRENT_YEAR",
}
export const DefaultDateViewTypeArray = [
  DefaultDateViewType.CURRENT_DAY,
  DefaultDateViewType.CURRENT_MONTH,
  DefaultDateViewType.CURRENT_WEEK,
  DefaultDateViewType.CURRENT_YEAR,
];

export const DefaultDateViewTypeMapping: Record<string, DefaultDateViewType[]> = {
  [IntervalType.DAY]: [DefaultDateViewType.CURRENT_DAY],
  [IntervalType.WEEK]: [DefaultDateViewType.CURRENT_WEEK],
  [IntervalType.MONTH]: [DefaultDateViewType.CURRENT_MONTH],
  [IntervalType.YEAR]: [DefaultDateViewType.CURRENT_YEAR],
};

export const enum DATE_FORMAT_TYPE {
  EXACT_WEEKDAY = "EXACT_WEEKDAY",
  EXACT_DAY_OF_MONTH = "EXACT_DAY_OF_MONTH",
  EXACT_DAY_OF_YEAR = "EXACT_DAY_OF_YEAR",
  EXACT_WEEK_OF_MONTH = "EXACT_WEEK_OF_MONTH",
  EXACT_WEEK_OF_YEAR = "EXACT_WEEK_OF_YEAR",
  EXACT_MONTH_OF_YEAR = "EXACT_MONTH_OF_YEAR",
  EXACT_QUARTER = "EXACT_QUARTER",
  EXACT_YEAR = "EXACT_YEAR",
  UPPERCASE = "UPPERCASE",
  LOWERCASE = "LOWERCASE",
}

export const DateFormatTypeArray = [
  DATE_FORMAT_TYPE.EXACT_WEEKDAY,
  DATE_FORMAT_TYPE.EXACT_DAY_OF_MONTH,
  DATE_FORMAT_TYPE.EXACT_DAY_OF_YEAR,
  DATE_FORMAT_TYPE.EXACT_WEEK_OF_MONTH,
  DATE_FORMAT_TYPE.EXACT_WEEK_OF_YEAR,
  DATE_FORMAT_TYPE.EXACT_MONTH_OF_YEAR,
  DATE_FORMAT_TYPE.EXACT_QUARTER,
  DATE_FORMAT_TYPE.EXACT_YEAR,
  DATE_FORMAT_TYPE.UPPERCASE,
  DATE_FORMAT_TYPE.LOWERCASE,
] as const;

export const FormatFieldMapping: Record<string, DATE_FORMAT_TYPE[]> = {
  [ElementType.DatePicker]: [
    DATE_FORMAT_TYPE.EXACT_WEEKDAY,
    DATE_FORMAT_TYPE.EXACT_DAY_OF_MONTH,
    DATE_FORMAT_TYPE.EXACT_DAY_OF_YEAR,
    DATE_FORMAT_TYPE.EXACT_WEEK_OF_MONTH,
    DATE_FORMAT_TYPE.EXACT_WEEK_OF_YEAR,
    DATE_FORMAT_TYPE.EXACT_MONTH_OF_YEAR,
    DATE_FORMAT_TYPE.EXACT_QUARTER,
    DATE_FORMAT_TYPE.EXACT_YEAR,
  ],
  [ElementType.SingleLineInputBox]: [DATE_FORMAT_TYPE.UPPERCASE, DATE_FORMAT_TYPE.LOWERCASE],
  [ElementType.Email]: [DATE_FORMAT_TYPE.UPPERCASE, DATE_FORMAT_TYPE.LOWERCASE],
  [ElementType.Name]: [DATE_FORMAT_TYPE.UPPERCASE, DATE_FORMAT_TYPE.LOWERCASE],
  [ElementType.SingleChoice]: [DATE_FORMAT_TYPE.UPPERCASE, DATE_FORMAT_TYPE.LOWERCASE],
  [ElementType.MultiChoice]: [DATE_FORMAT_TYPE.UPPERCASE, DATE_FORMAT_TYPE.LOWERCASE],
  [ElementType.SingleDropdownSelect]: [DATE_FORMAT_TYPE.UPPERCASE, DATE_FORMAT_TYPE.LOWERCASE],
  [ElementType.MultipleDropdownSelect]: [DATE_FORMAT_TYPE.UPPERCASE, DATE_FORMAT_TYPE.LOWERCASE],
};
